import { getColDef } from '@/blocks/components/DataGrid/shared/getColDef';
import { WithTrendColor } from '@/blocks/components/WithTrendColor';
import { FormatPercent, FormatPrice } from '@/blocks/formatters';
import { FormatPriceWithPlaceholder } from '@/blocks/formatters/format-price';

import styles from '../styles.module.scss';
import {
  isTradable,
  renderCellAsset,
  renderCellAssetTrend,
  renderCellCta,
  renderCellPrice,
  renderCellPriceAndChange,
} from './cells';
import { AssetGridRowModel } from './rowModel';

export const getColDefMarket = (headerName: string) =>
  getColDef<AssetGridRowModel>('name', headerName, renderCellAsset, {
    sortComparator: (a, b) => b.localeCompare(a),
    flex: 1.2,
    minWidth: 150,
  });

export const getColDefPriceAndChange = (headerName: string) =>
  getColDef<AssetGridRowModel>('priceAndChange', headerName, renderCellPriceAndChange, {
    headerClassName: styles.priceAndChangeHeader,
    sortComparator: (a, b) => a?.price - b?.price,
  });

export const getColDefBuy = (headerName: string) =>
  getColDef<AssetGridRowModel>('bid', headerName, (params) => (
    <FormatPrice price={params.row.bid} priceScale={params.row.priceScale} />
  ));

export const getColDefSell = (headerName: string) =>
  getColDef<AssetGridRowModel>('ask', headerName, (params) =>
    isTradable(params.row) ? <FormatPrice price={params.row.ask} priceScale={params.row.priceScale} /> : null,
  );

export const getColDefSpread = (headerName: string) =>
  getColDef<AssetGridRowModel>('spread', headerName, (params) => {
    return isTradable(params.row) ? <FormatPrice price={params.row.spreadInPips} priceScale={0} /> : null;
  });

export const getColDef24hChange = (headerName: string) =>
  getColDef<AssetGridRowModel>(
    'change24h',
    headerName,
    (params) =>
      isTradable(params.row) ? (
        <WithTrendColor valueToCalculateTrend={params.row.change24h}>
          <FormatPercent value={params.row.change24h} forcePlusSign={true} />
        </WithTrendColor>
      ) : null,

    {
      flex: 0.9,
    },
  );

export const getColDefTrend = (headerName: string) =>
  getColDef<AssetGridRowModel>('trendChart', headerName, renderCellAssetTrend, {
    sortComparator: (a, b) => b?.trendComparatorValue - a?.trendComparatorValue,
  });

export const getColDefCta = (cta: { text: string; enText: string }) =>
  getColDef<AssetGridRowModel>('cta', '', renderCellCta(cta), {
    align: 'right',
    sortable: false,
    minWidth: 160,
  });

export const getColDefPrice = (headerName: string) =>
  getColDef<AssetGridRowModel>('price', headerName, renderCellPrice, {
    sortComparator: (a, b) => a - b,
  });

export const getColDefVol24 = (headerName: string) =>
  getColDef<AssetGridRowModel>(
    'vol24',
    headerName,
    (params) => {
      return isTradable(params.row) ? (
        <FormatPrice price={params.row.vol24} priceScale={params.row.qtyScale} />
      ) : null;
    },
    {
      sortComparator: (a, b) => a - b,
    },
  );

export const getColDefMarketCap = (headerName: string) =>
  getColDef<AssetGridRowModel>(
    'marketcap',
    headerName,
    (params) => {
      return isTradable(params.row) ? (
        <FormatPriceWithPlaceholder
          price={params.row.marketCapInfo?.market_cap}
          compact={true}
          priceScale={2}
        />
      ) : null;
    },
    { flex: 0.9 },
  );

export const getColDefHighPrice24h = (headerName: string) =>
  getColDef<AssetGridRowModel>(
    'highPrice24h',
    headerName,
    (params) => {
      return isTradable(params.row) ? (
        <FormatPrice price={params.row.highPrice24h} priceScale={params.row.priceScale} />
      ) : null;
    },
    { sortComparator: (a, b) => a - b },
  );

export const getColDefLowPrice24h = (headerName: string) =>
  getColDef<AssetGridRowModel>(
    'lowPrice24h',
    headerName,
    (params) => {
      return isTradable(params.row) ? (
        <FormatPrice price={params.row.lowPrice24h} priceScale={params.row.priceScale} />
      ) : null;
    },
    { sortComparator: (a, b) => a - b },
  );
